import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import styles from './Checkout.module.css';

export default function Checkout() {
  const navigate = useNavigate();
  const { state, dispatch } = useBooking();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cardNumber: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    cardNumber: ''
  });

  // Redirect if no selected flight
  useEffect(() => {
    if (!state.selectedFlight) {
      navigate('/');
    }
  }, [state.selectedFlight, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
      email: '',
      cardNumber: ''
    };

    let isValid = true;

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
      isValid = false;
    }

    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
        isValid = false;
      }
    }

    // Validate card number
    if (!formData.cardNumber.trim()) {
      newErrors.cardNumber = 'Card number is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Set passenger data
    dispatch({
      type: 'SET_PASSENGER',
      payload: {
        name: formData.name,
        email: formData.email
      }
    });

    // Show success modal
    setShowSuccess(true);
  };

  const handleBookAnother = () => {
    dispatch({ type: 'RESET_BOOKING' });
    navigate('/');
  };

  if (!state.selectedFlight) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.grid}>
        <div>
          <form onSubmit={handleSubmit}>
            {/* Passenger Information */}
            <div className={styles.formSection}>
              <h3>Passenger Information</h3>
              <div className={styles.formGroup}>
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? styles.error : ''}
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "name-error" : undefined}
                  required
                />
                {errors.name && (
                  <span id="name-error" className={styles.errorMessage} role="alert">
                    {errors.name}
                  </span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? styles.error : ''}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  required
                />
                {errors.email && (
                  <span id="email-error" className={styles.errorMessage} role="alert">
                    {errors.email}
                  </span>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div className={styles.formSection}>
              <h3>Payment Information</h3>
              <div className={styles.formGroup}>
                <label htmlFor="cardNumber">Card Number (Demo) *</label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={formData.cardNumber}
                  onChange={handleInputChange}
                  placeholder="Any card number works for demo"
                  className={errors.cardNumber ? styles.error : ''}
                  aria-invalid={!!errors.cardNumber}
                  aria-describedby={errors.cardNumber ? "cardNumber-error" : undefined}
                  required
                />
                {errors.cardNumber && (
                  <span id="cardNumber-error" className={styles.errorMessage} role="alert">
                    {errors.cardNumber}
                  </span>
                )}
              </div>
              <button
                type="submit"
                className={styles.submitButton}
              >
                Complete Booking
              </button>
            </div>
          </form>
        </div>

        {/* Flight Summary */}
        <div className={styles.summaryCard}>
          <h3>Flight Summary</h3>
          <div className={styles.flightDetails}>
            <div className={styles.flightNumber}>
              {state.selectedFlight.airline} {state.selectedFlight.flightNumber}
            </div>
            <div className={styles.route}>
              {state.selectedFlight.departure.airport} → {state.selectedFlight.arrival.airport}
            </div>
            <div className={styles.time}>
              Departure: {state.selectedFlight.departure.time}
            </div>
            <div className={styles.time}>
              Arrival: {state.selectedFlight.arrival.time}
            </div>
            <div className={styles.time}>
              Duration: {state.selectedFlight.duration}
            </div>
          </div>
          <div className={styles.priceSection}>
            <div className={styles.totalPrice}>
              <span>Total Price:</span>
              <span>${state.selectedFlight.price}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Booking Confirmed!</h3>
            <p>
              Your flight has been successfully booked. A confirmation email
              will be sent to {formData.email} shortly.
            </p>
            <button
              className={styles.modalButton}
              onClick={handleBookAnother}
            >
              Book Another Flight
            </button>
          </div>
        </div>
      )}
    </div>
  );
}